import { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/contexts/ToastContext';

const PRICING_BY_COUNTRY = {
  MA: { // Morocco
    currency: 'MAD',
    symbol: 'MAD',
    plans: {
      CONNECT: 150,
      PRO_SUIVI: 350,
      SERENITE: 500,
    },
  },
  DEFAULT: { // Rest of world (France prices)
    currency: 'EUR',
    symbol: '€',
    plans: {
      CONNECT: 59,
      PRO_SUIVI: 89,
      SERENITE: 129,
    },
  },
};

export default function PricingPage() {
  const { t } = useTranslation();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [pricing, setPricing] = useState(PRICING_BY_COUNTRY.DEFAULT);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const token = useAuthStore((state) => state.token);
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const countryCode = data.country_code;
        setPricing(PRICING_BY_COUNTRY[countryCode as keyof typeof PRICING_BY_COUNTRY] || PRICING_BY_COUNTRY.DEFAULT);
      } catch {
        setPricing(PRICING_BY_COUNTRY.DEFAULT);
      }
    };
    detectCountry();
  }, []);

  if (user?.role === 'STAFF') {
    return <Navigate to="/staff" replace />;
  }

  const PLANS = [
    {
      id: 'CONNECT',
      name: 'CONNECT',
      price: pricing.plans.CONNECT,
      highlighted: false,
      features: [
        t('pricing.features.allModules'),
        t('pricing.features.tablet'),
        t('pricing.features.remoteSetup'),
        t('pricing.features.support'),
        t('pricing.features.commitment'),
      ],
    },
    {
      id: 'PRO_SUIVI',
      name: 'PRO SUIVI',
      price: pricing.plans.PRO_SUIVI,
      highlighted: true,
      badge: t('pricing.mostPopular'),
      features: [
        t('pricing.features.allModules'),
        t('pricing.features.tablet'),
        t('pricing.features.tempSensor'),
        t('pricing.features.remoteSetup'),
        t('pricing.features.support'),
        t('pricing.features.commitment'),
      ],
    },
    {
      id: 'SERENITE',
      name: 'SÉRÉNITÉ',
      price: pricing.plans.SERENITE,
      highlighted: false,
      features: [
        t('pricing.features.allModules'),
        t('pricing.features.tablet'),
        t('pricing.features.tempSensor'),
        t('pricing.features.dlcPrinter'),
        t('pricing.features.onsiteSetup'),
        t('pricing.features.support'),
        t('pricing.features.commitment'),
      ],
    },
  ];

  const wasCanceled = searchParams.get('status') === 'canceled';

  const handleSubscribe = async (planId: string) => {
    if (!token) {
      navigate('/register');
      return;
    }

    setLoadingPlan(planId);
    try {
      const origin = window.location.origin;
      const { url } = await apiClient.createCheckoutSession(
        `${origin}/checkout/success`,
        `${origin}/pricing?status=canceled`,
        planId,
      );
      window.location.href = url;
    } catch (err: any) {
      const rawMsg = err?.response?.data?.message;
      const msg = typeof rawMsg === 'string'
        ? rawMsg
        : Array.isArray(rawMsg)
          ? rawMsg.join(', ')
          : t('pricing.checkoutError');
      addToast(msg, 'error');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-navy via-primary-800 to-primary-600 flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-20 w-96 h-96 bg-green-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-10">
          {token && (
            <div className="flex justify-end mb-4">
              <button
                onClick={() => { logout(); navigate('/login'); }}
                className="px-4 py-2 text-sm font-medium text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-300 border border-white/20 hover:border-white/40"
              >
                {t('pricing.logout')}
              </button>
            </div>
          )}
          <div className="flex items-center justify-center mb-4">
            <img src="/logo.png" alt="ZIDO HACCP" className="h-14 w-auto" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('pricing.title')}</h1>
          <p className="text-white/70">{t('pricing.subtitle')}</p>
        </div>

        {wasCanceled && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center max-w-md mx-auto">
            <p className="text-sm font-medium text-yellow-800">
              {t('pricing.canceledMessage')}
            </p>
          </div>
        )}

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-transform hover:-translate-y-1 ${
                plan.highlighted ? 'ring-2 ring-primary-500 scale-[1.02]' : ''
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-0 left-0 right-0">
                  <div className="bg-primary-500 text-white text-xs font-bold text-center py-1.5 uppercase tracking-wider">
                    {plan.badge}
                  </div>
                </div>
              )}

              {/* Plan header */}
              <div className={`px-6 pt-8 pb-5 text-center ${plan.highlighted ? 'pt-12' : ''}`}>
                <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-3">{plan.name}</h3>
                <div className="flex items-end justify-center gap-1">
                  <span className="text-5xl font-extrabold text-neutral-900">{plan.price}</span>
                  <span className="text-xl font-bold text-neutral-900">{pricing.symbol}</span>
                  <span className="text-neutral-500 text-sm mb-1">{t('pricing.monthly')}</span>
                </div>
              </div>

              <div className="h-px bg-neutral-100 mx-6" />

              {/* Features */}
              <div className="px-6 py-5 flex-1">
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-neutral-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="px-6 pb-6">
                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loadingPlan !== null}
                  className={`w-full py-3 px-6 font-bold text-base rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
                    plan.highlighted
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200'
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('pricing.loading')}
                    </span>
                  ) : (
                    t('pricing.subscribe')
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-center text-white/40 mt-6">
          {t('pricing.securePayment')}
        </p>

        {!token && (
          <p className="text-center text-sm text-white/80 mt-6">
            {t('pricing.alreadyAccount')}{' '}
            <button onClick={() => navigate('/login')} className="text-white font-semibold underline hover:no-underline">
              {t('pricing.login')}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
